// src/shared_new/modules/QuickCostButton.jsx
import React from "react";
import { Box, Button, CircularProgress, Typography, useTheme } from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

/**
 * QuickCostButton - Floating Action Button
 *
 * Expandable FAB that triggers cost calculation
 * Hovers on bottom-right with expand animation on hover
 *
 * @param {function} onClick - Callback when button is clicked
 * @param {boolean} disabled - Whether button is disabled
 * @param {boolean} loading - Whether to show loading state
 * @param {string} brandColor - Brand color for button (optional override)
 */
export default function QuickCostButton({
  onClick,
  disabled = false,
  loading = false,
  brandColor,
}) {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {};
  const buttonColor = brandColor || perlColors.brandColor || "#ff6034";

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 32,
        right: 32,
        zIndex: 1000,
        "&:hover .fab-button": {
          width: "180px",
          minWidth: "180px",
        },
        "&:hover .fab-icon": {
          opacity: 0,
          transform: "scale(0)",
        },
        "&:hover .fab-text": {
          opacity: 1,
          transform: "translateX(0)",
        },
      }}
    >
      <Button
        className="fab-button"
        variant="contained"
        size="large"
        onClick={onClick}
        disabled={disabled || loading}
        sx={{
          mr: 2,
          bgcolor: buttonColor,
          color: perlColors.textOnBrand || "#000",
          borderRadius: "999px",
          minWidth: "50px",
          width: "50px",
          height: "50px",
          padding: 0,
          overflow: "hidden",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            bgcolor: buttonColor,
          },
          "&:disabled": {
            bgcolor: perlColors.disabled || "#666",
            color: perlColors.textSecondary || "#999",
          },
        }}
      >
        {/* Icon - visible when collapsed */}
        <Box
          className="fab-icon"
          sx={{
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: perlColors.textOnBrand || "#000" }} />
          ) : (
            <AutoFixHighIcon
              sx={{ fontSize: "1.75rem", color: perlColors.textOnBrand || "#000" }}
            />
          )}
        </Box>

        {/* Text - visible when expanded */}
        <Box
          className="fab-text"
          sx={{
            position: "absolute",
            display: "flex",
            alignItems: "center",
            gap: 1,
            opacity: 0,
            transform: "translateX(20px)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            whiteSpace: "nowrap",
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ color: perlColors.textOnBrand || "#000" }} />
              <Typography sx={{ fontWeight: 600, color: perlColors.textOnBrand || "#000" }}>
                Computing...
              </Typography>
            </>
          ) : (
            <>
              <AutoFixHighIcon sx={{ color: perlColors.textOnBrand || "#000" }} />
              <Typography sx={{ fontWeight: 600, color: perlColors.textOnBrand || "#000" }}>
                Quick Cost
              </Typography>
            </>
          )}
        </Box>
      </Button>
    </Box>
  );
}
